// src/components/admin/EditableValueChainTable.tsx - Fully Editable Value Chain Table
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Chip, 
  useTheme, 
  alpha,
  Tooltip,
  Fade,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  ArrowForward as ArrowForwardIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  TextFields as TextFieldsIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { valueChains } from '../../data/valueChainData';
import { updateValueChainStage } from '../../services/industry';

// Editable Text Component
const EditableText = ({ 
  value, 
  onSave, 
  variant = 'body1', 
  multiline = false, 
  rows = 1, 
  placeholder = 'Click to edit...',
  fontWeight,
  color,
  sx = {},
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const theme = useTheme();

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  if (disabled) {
    return (
      <Typography variant={variant} sx={{ fontWeight, color, ...sx }}>
        {value || placeholder}
      </Typography>
    );
  }

  if (isEditing) {
    return (
      <Box sx={{ position: 'relative', ...sx }}>
        <TextField
          fullWidth
          multiline={multiline}
          rows={rows}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          onBlur={handleSave}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !multiline) {
              handleSave();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '& fieldset': {
                borderColor: theme.palette.primary.main,
              }
            }
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                <IconButton size="small" onClick={handleSave} color="primary">
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )
          }}
          autoFocus
        />
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        position: 'relative',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          '& .edit-indicator': {
            opacity: 1
          }
        },
        p: 0.5,
        borderRadius: 1,
        minHeight: '1.5em',
        ...sx
      }}
      onClick={() => setIsEditing(true)}
    >
      <Typography 
        variant={variant} 
        sx={{ 
          fontWeight, 
          color, 
          ...(value ? {} : { 
            color: theme.palette.text.disabled,
            fontStyle: 'italic'
          })
        }}
      >
        {value || placeholder}
      </Typography>
      <TextFieldsIcon 
        className="edit-indicator"
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          fontSize: 12,
          opacity: 0,
          transition: 'opacity 0.2s',
          color: theme.palette.primary.main
        }}
      />
    </Box>
  );
};

// Editable List Component
const EditableList = ({ 
  items, 
  onUpdate, 
  title, 
  allowAdd = true, 
  allowDelete = true 
}) => {
  const theme = useTheme();
  
  const handleItemChange = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onUpdate(newItems);
  };

  const handleAddItem = () => {
    onUpdate([...items, '']);
  };

  const handleDeleteItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onUpdate(newItems);
  };

  return (
    <Box sx={{ mt: 2 }}>
      {title && (
        <Typography variant="h6" fontWeight={600} color="primary.main" gutterBottom>
          {title}
        </Typography>
      )}
      <List sx={{ mt: 1 }}>
        {items.map((item, index) => (
          <ListItem key={index} sx={{ py: 1, px: 0 }}>
            <ListItemText
              primary={
                <EditableText
                  value={item}
                  onSave={(value) => handleItemChange(index, value)}
                  placeholder="Enter item..."
                />
              }
            />
            {allowDelete && (
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteItem(index)}
                  sx={{ color: theme.palette.error.main }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
      {allowAdd && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Add Item
        </Button>
      )}
    </Box>
  );
};

const EditableValueChainTable = ({ valueChainId, valueChainData: propValueChainData, onNavigate, onUpdate, industryId, subIndustryId }) => {
  const theme = useTheme();
  const [hoveredCell, setHoveredCell] = useState(null);
  const [detailStage, setDetailStage] = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [valueChainData, setValueChainData] = useState(() => {
    // Use prop data if provided, otherwise check local data, otherwise use default structure
    return propValueChainData || valueChains[valueChainId] || {
      stages: [
        {
          name: 'Import',
          content: '',
          categories: {},
          order: 0
        },
        {
          name: 'Research & Discovery',
          content: '',
          categories: {},
          order: 1
        },
        {
          name: 'Development',
          content: '',
          categories: {},
          order: 2
        },
        {
          name: 'Manufacturing',
          content: '',
          categories: {},
          order: 3
        },
        {
          name: 'Commercialization',
          content: '',
          categories: {},
          order: 4
        },
        {
          name: 'Export',
          content: '',
          categories: {},
          order: 5
        }
      ],
      additionalContent: []
    };
  });

  // Use ref to track if we're syncing from props to prevent infinite loop
  const isSyncingFromProps = React.useRef(false);
  const prevDataRef = React.useRef();

  // Update local state when prop changes
  React.useEffect(() => {
    // Only sync if data actually changed and we're not already syncing
    if (propValueChainData && JSON.stringify(prevDataRef.current) !== JSON.stringify(propValueChainData)) {
      isSyncingFromProps.current = true;
      prevDataRef.current = propValueChainData;
      setValueChainData(propValueChainData);
      // Reset flag after state update completes
      setTimeout(() => {
        isSyncingFromProps.current = false;
      }, 0);
    }
  }, [propValueChainData]);

  // Update parent when data changes (but not when syncing from props)
  React.useEffect(() => {
    if (onUpdate && !isSyncingFromProps.current) {
      onUpdate(valueChainData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueChainData]);

  // Function to split content by commas and create separate items
  const splitContent = (content) => {
    if (!content) return [];
    return content.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  // Get categories from all stages to create consistent rows
  const getAllCategories = () => {
    const categories = new Set();
    valueChainData.stages.forEach(stage => {
      if (stage.categories) {
        Object.keys(stage.categories).forEach(category => {
          categories.add(category);
        });
      }
    });
    const categoriesArray = Array.from(categories);

    // Ensure 'Sub-Industries' is always first if it exists
    const subIndustriesIndex = categoriesArray.indexOf('Sub-Industries');
    if (subIndustriesIndex > 0) {
      categoriesArray.splice(subIndustriesIndex, 1);
      categoriesArray.unshift('Sub-Industries');
    }

    return categoriesArray;
  };

  const categories = getAllCategories();

  // Generate a color for the header based on position
  const getHeaderColor = (index, totalStages) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.primary.dark,      
      theme.palette.secondary.main,
      theme.palette.secondary.dark,    
      theme.palette.primary.dark
    ];
    
    if (totalStages <= colors.length) {
      return colors[index % colors.length];
    }
    
    const position = index / (totalStages - 1);
    const segmentCount = colors.length - 1;
    const segment = Math.min(Math.floor(position * segmentCount), segmentCount - 1);
    return colors[segment];
  };

  const getCellHighlightColor = (index, totalStages, isHeader = false) => {
    if (isHeader) {
      return getHeaderColor(index, totalStages);
    }
    return alpha(getHeaderColor(index, totalStages), 0.05);
  };

  // Calculate fixed column width for consistent alignment
  const columnWidth = `${100 / valueChainData.stages.length}%`;
  const minColumnWidth = "150px";

  // Handle clicking on a stage header for detailed editing
  const handleStageClick = (stage, index) => {
    setDetailStage({ ...stage, index });
  };

  // Handle editing a specific stage
  const handleEditStage = (stage, index) => {
    setEditingStage({ ...stage, index });
  };

  // Save stage changes
  const handleSaveStage = async (updatedStage) => {
    try {
      const stageData = {
        name: updatedStage.name,
        content: updatedStage.content,
        categories: updatedStage.categories,
        order: updatedStage.order,
        additionalData: {
          summary: updatedStage.summary,
          topImportCategories: updatedStage.topImportCategories,
          dominantSuppliers: updatedStage.dominantSuppliers,
          criticalDependencies: updatedStage.criticalDependencies,
          importReductionPlan: updatedStage.importReductionPlan,
          productsServicesNeeded: updatedStage.productsServicesNeeded,
          keyPlayers: updatedStage.keyPlayers,
          trends: updatedStage.trends,
          challenges: updatedStage.challenges,
          riskRating: updatedStage.riskRating,
          policyRecommendations: updatedStage.policyRecommendations
        }
      };

      // If industryId is provided, save to API
      if (industryId && updatedStage.id) {
        await updateValueChainStage(industryId, updatedStage.id, stageData);
      }

      // Update local state
      const newStages = [...valueChainData.stages];
      newStages[updatedStage.index] = {
        ...updatedStage,
        ...stageData,
        ...stageData.additionalData
      };

      setValueChainData(prev => ({
        ...prev,
        stages: newStages
      }));

      setEditingStage(null);
      setDetailStage(null);
    } catch (error) {
      console.error('Error saving stage:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Add new stage
  const handleAddStage = () => {
    const newStage = {
      name: `New Stage ${valueChainData.stages.length + 1}`,
      content: '',
      categories: {}
    };

    setValueChainData(prev => ({
      ...prev,
      stages: [...prev.stages, newStage]
    }));
  };

  // Delete stage
  const handleDeleteStage = (index) => {
    const newStages = valueChainData.stages.filter((_, i) => i !== index);
    setValueChainData(prev => ({
      ...prev,
      stages: newStages
    }));
  };

  // Add new row (category)
  const handleAddCategory = () => {
    // Generate a unique category name
    const categoryName = `Row ${categories.length + 1}`;

    // Add the category to all stages with empty content
    const newStages = valueChainData.stages.map(stage => ({
      ...stage,
      categories: {
        ...stage.categories,
        [categoryName]: ''
      }
    }));

    setValueChainData(prev => ({
      ...prev,
      stages: newStages
    }));
  };

  // Update cell content
  const updateCellContent = (stageIndex, category, itemIndex, value) => {
    const newStages = [...valueChainData.stages];
    const stage = newStages[stageIndex];
    
    if (!stage.categories) {
      stage.categories = {};
    }
    
    if (!stage.categories[category]) {
      stage.categories[category] = '';
    }

    const items = splitContent(stage.categories[category]);
    items[itemIndex] = value;
    stage.categories[category] = items.filter(item => item.trim() !== '').join(', ');

    setValueChainData(prev => ({
      ...prev,
      stages: newStages
    }));
  };

  // Add new item to category
  const addItemToCategory = (stageIndex, category) => {
    const newStages = [...valueChainData.stages];
    const stage = newStages[stageIndex];
    
    if (!stage.categories) {
      stage.categories = {};
    }
    
    const currentContent = stage.categories[category] || '';
    const items = splitContent(currentContent);
    items.push('');
    stage.categories[category] = items.join(', ');

    setValueChainData(prev => ({
      ...prev,
      stages: newStages
    }));
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setDetailStage(null);
  };

  // Render the detail/edit view for a stage
  const renderDetailView = () => {
    if (!detailStage && !editingStage) return null;

    const stage = editingStage || detailStage;
    const stageIndex = stage.index;
    const headerColor = getHeaderColor(stageIndex, valueChainData.stages.length);
    const isEditing = !!editingStage;

    return (
      <Modal
        open={!!(detailStage || editingStage)}
        onClose={() => {
          setDetailStage(null);
          setEditingStage(null);
        }}
        aria-labelledby="stage-detail-title"
        BackdropProps={{ 
          style: { backgroundColor: alpha('#000', 0.75) }
        }}
      >
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            overflow: 'auto',
            p: 0,
            borderRadius: 3,
            boxShadow: 24,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, 
                ${headerColor}, 
                ${alpha(headerColor, 0.85)})`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#fff',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}
          >
            <Box sx={{ flex: 1 }}>
              {isEditing ? (
                <TextField
                  value={stage.name}
                  onChange={(e) => setEditingStage(prev => ({ ...prev, name: e.target.value }))}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: alpha('#fff', 0.3) },
                      '&:hover fieldset': { borderColor: alpha('#fff', 0.5) },
                      '&.Mui-focused fieldset': { borderColor: 'white' }
                    }
                  }}
                />
              ) : (
                <Typography 
                  id="stage-detail-title"
                  variant="h5" 
                  component="h2"
                  fontWeight="bold"
                >
                  {stage.name}
                </Typography>
              )}
              <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                Stage {stageIndex + 1} of {valueChainData.stages.length}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!isEditing && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditingStage(stage);
                    setDetailStage(null);
                  }}
                  sx={{ 
                    color: '#fff', 
                    borderColor: '#fff',
                    '&:hover': { borderColor: '#fff', bgcolor: alpha('#fff', 0.1) }
                  }}
                >
                  Edit
                </Button>
              )}
              <IconButton 
                onClick={() => {
                  setDetailStage(null);
                  setEditingStage(null);
                }}
                sx={{ color: '#fff' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            {/* Summary */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="medium" gutterBottom>
                Summary
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.7),
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={stage.summary || ''}
                    onChange={(e) => setEditingStage(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="Enter stage summary..."
                  />
                ) : (
                  <Typography variant="body1">
                    {stage.summary || 'No summary available'}
                  </Typography>
                )}
              </Paper>
            </Box>

            {/* Top Import Categories */}
            {(isEditing || stage.topImportCategories) && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Top Import Categories
                </Typography>
                {isEditing ? (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Headers (comma-separated):</Typography>
                    <TextField
                      fullWidth
                      value={stage.topImportCategories?.headers?.join(', ') || ''}
                      onChange={(e) => {
                        const headers = e.target.value.split(',').map(h => h.trim());
                        setEditingStage(prev => ({
                          ...prev,
                          topImportCategories: {
                            ...prev.topImportCategories,
                            headers: headers
                          }
                        }));
                      }}
                      placeholder="Category, Share of Imports, Top Supplier Countries"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="subtitle2" gutterBottom>Rows:</Typography>
                    {stage.topImportCategories?.rows?.map((row, idx) => (
                      <TextField
                        key={idx}
                        fullWidth
                        value={row.join(', ')}
                        onChange={(e) => {
                          const newRow = e.target.value.split(',').map(cell => cell.trim());
                          const newRows = [...(stage.topImportCategories?.rows || [])];
                          newRows[idx] = newRow;
                          setEditingStage(prev => ({
                            ...prev,
                            topImportCategories: {
                              ...prev.topImportCategories,
                              rows: newRows
                            }
                          }));
                        }}
                        placeholder="Enter row data (comma-separated)"
                        sx={{ mb: 1 }}
                      />
                    )) || []}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newRows = [...(stage.topImportCategories?.rows || []), ['', '', '']];
                        setEditingStage(prev => ({
                          ...prev,
                          topImportCategories: {
                            ...prev.topImportCategories,
                            rows: newRows
                          }
                        }));
                      }}
                      sx={{ mt: 1 }}
                    >
                      Add Row
                    </Button>
                  </Paper>
                ) : (
                  stage.topImportCategories && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            {stage.topImportCategories.headers?.map((header, idx) => (
                              <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stage.topImportCategories.rows?.map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.background.default, 0.5) } }}>
                              {row.map((cell, cellIdx) => (
                                <TableCell key={cellIdx}>{cell}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                )}
              </Box>
            )}

            {/* Editable Lists for other properties */}
            {['dominantSuppliers', 'criticalDependencies', 'productsServicesNeeded', 'keyPlayers', 'policyRecommendations'].map(property => (
              (isEditing || stage[property]) && (
                <Box key={property} sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="medium" gutterBottom>
                    {property.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </Typography>
                  {isEditing ? (
                    <EditableList
                      items={stage[property] || []}
                      onUpdate={(newItems) => setEditingStage(prev => ({ ...prev, [property]: newItems }))}
                    />
                  ) : (
                    <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
                      {stage[property]?.map((item, idx) => (
                        <Box 
                          key={idx}
                          sx={{ 
                            p: 2,
                            backgroundColor: idx % 2 === 0 ? 'transparent' : alpha(theme.palette.background.default, 0.5),
                            borderBottom: idx < stage[property].length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                          }}
                        >
                          <Typography variant="body2" dangerouslySetInnerHTML={{ __html: item }} />
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>
              )
            ))}

            {/* Single text fields */}
            {['trends', 'challenges', 'riskRating'].map(property => (
              (isEditing || stage[property]) && (
                <Box key={property} sx={{ mb: 4 }}>
                  <Typography variant="h6" fontWeight="medium" gutterBottom>
                    {property.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: (() => {
                        if (property === 'riskRating') {
                          if (stage[property]?.includes('High') || stage[property]?.includes('Critical')) {
                            return alpha(theme.palette.error.main, 0.05);
                          } else if (stage[property]?.includes('Medium')) {
                            return alpha(theme.palette.warning.main, 0.05);
                          }
                          return alpha(theme.palette.success.main, 0.05);
                        }
                        return alpha(theme.palette.primary.main, 0.05);
                      })(),
                      border: `1px solid ${(() => {
                        if (property === 'riskRating') {
                          if (stage[property]?.includes('High') || stage[property]?.includes('Critical')) {
                            return alpha(theme.palette.error.main, 0.1);
                          } else if (stage[property]?.includes('Medium')) {
                            return alpha(theme.palette.warning.main, 0.1);
                          }
                          return alpha(theme.palette.success.main, 0.1);
                        }
                        return alpha(theme.palette.primary.main, 0.1);
                      })()}`
                    }}
                  >
                    {isEditing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={stage[property] || ''}
                        onChange={(e) => setEditingStage(prev => ({ ...prev, [property]: e.target.value }))}
                        placeholder={`Enter ${property}...`}
                      />
                    ) : (
                      <Typography variant="body1" fontWeight={property === 'riskRating' ? 500 : 400}>
                        {stage[property]}
                      </Typography>
                    )}
                  </Paper>
                </Box>
              )
            ))}

            {/* Import Reduction Plan */}
            {(isEditing || stage.importReductionPlan) && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="medium" gutterBottom>
                  Import Reduction Plan
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.05),
                    borderColor: alpha(theme.palette.success.main, 0.2)
                  }}
                >
                  {isEditing ? (
                    <>
                      <TextField
                        fullWidth
                        value={stage.importReductionPlan?.text || ''}
                        onChange={(e) => setEditingStage(prev => ({
                          ...prev,
                          importReductionPlan: {
                            ...prev.importReductionPlan,
                            text: e.target.value
                          }
                        }))}
                        placeholder="Enter plan description..."
                        sx={{ mb: 2 }}
                      />
                      <EditableList
                        items={stage.importReductionPlan?.points || []}
                        onUpdate={(newItems) => setEditingStage(prev => ({
                          ...prev,
                          importReductionPlan: {
                            ...prev.importReductionPlan,
                            points: newItems
                          }
                        }))}
                        title="Action Points"
                      />
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" fontWeight="medium" gutterBottom>
                        {stage.importReductionPlan?.text}
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                        {stage.importReductionPlan?.points?.map((point, idx) => (
                          <Typography key={idx} component="li" variant="body2" sx={{ mb: 1 }}>
                            {point}
                          </Typography>
                        ))}
                      </Box>
                    </>
                  )}
                </Paper>
              </Box>
            )}

            {/* Navigation and Save buttons */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${theme.palette.divider}`
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setDetailStage(null);
                  setEditingStage(null);
                }}
              >
                Back to Table
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {isEditing && (
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSaveStage(editingStage)}
                    color="primary"
                  >
                    Save Changes
                  </Button>
                )}
                {stageIndex > 0 && !isEditing && (
                  <Button
                    variant="outlined"
                    onClick={() => handleStageClick(valueChainData.stages[stageIndex - 1], stageIndex - 1)}
                  >
                    Previous Stage
                  </Button>
                )}
                {stageIndex < valueChainData.stages.length - 1 && !isEditing && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleStageClick(valueChainData.stages[stageIndex + 1], stageIndex + 1)}
                    sx={{ backgroundColor: getHeaderColor(stageIndex + 1, valueChainData.stages.length) }}
                  >
                    Next Stage
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Modal>
    );
  };
  
  return (
    <Box sx={{ mt: 2, width: '100%' }}>
      {/* Control Panel */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: alpha(theme.palette.background.default, 0.5), borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Value Chain Editor
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddStage}
              size="small"
            >
              Add Stage
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
              size="small"
            >
              Add Row
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Click on any cell to edit content. Click stage headers for detailed editing.
        </Typography>
      </Box>

      {/* Container with horizontal scroll */}
      <Box 
        sx={{ 
          position: 'relative',
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Headers - Fixed position */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            overflow: 'hidden'
          }}
        >
          {valueChainData.stages.map((stage, index) => (
            <Box
              key={index}
              sx={{
                width: columnWidth,
                minWidth: minColumnWidth,
                p: 2.5,
                background: `linear-gradient(135deg, 
                  ${getCellHighlightColor(index, valueChainData.stages.length, true)}, 
                  ${alpha(getCellHighlightColor(index, valueChainData.stages.length, true), 0.85)})`,
                borderRight: index < valueChainData.stages.length - 1 ? `1px solid ${alpha('#fff', 0.1)}` : 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                position: 'relative',
                transition: 'all 0.2s ease',
                "&:hover": {
                  boxShadow: `0 4px 12px ${alpha(getCellHighlightColor(index, valueChainData.stages.length, true), 0.4)}`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <EditableText
                value={stage.name}
                onSave={(value) => {
                  const newStages = [...valueChainData.stages];
                  newStages[index].name = value;
                  setValueChainData(prev => ({ ...prev, stages: newStages }));
                }}
                variant="subtitle1"
                fontWeight={600}
                color="white"
                placeholder="Enter stage name..."
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  maxWidth: '100%',
                  padding: '0 4px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  textAlign: 'center'
                }}
              />
              
              {/* Action buttons */}
              <Box sx={{ 
                position: 'absolute', 
                top: 4, 
                right: 4, 
                display: 'flex', 
                gap: 0.5,
                opacity: 0,
                transition: 'opacity 0.2s',
                '.MuiBox-root:hover &': { opacity: 1 }
              }}>
                <IconButton
                  size="small"
                  onClick={() => handleStageClick(stage, index)}
                  sx={{ 
                    color: 'white', 
                    backgroundColor: alpha('#fff', 0.2),
                    '&:hover': { backgroundColor: alpha('#fff', 0.3) }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                {valueChainData.stages.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteStage(index)}
                    sx={{ 
                      color: 'white', 
                      backgroundColor: alpha('#fff', 0.2),
                      '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.6) }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* Scrollable Content Area */}
        <Box 
          sx={{ 
            overflowX: 'auto',
            maxHeight: '600px',
            overflowY: 'auto',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.primary.main, 0.2),
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.4),
              }
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(theme.palette.grey[200], 0.4),
              borderRadius: '4px',
            },
          }}
        >
          <Paper 
            elevation={0}
            sx={{
              minWidth: valueChainData.stages.length > 5 ? '1200px' : '100%',
              borderRadius: 3,
              overflow: 'hidden',
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            }}
          >
            {/* Rows for each category */}
            {categories.map((category, rowIndex) => (
              <React.Fragment key={rowIndex}>
                {/* Dynamic rows based on category content */}
                {(() => {
                  // Find maximum number of items in this category across all stages
                  let maxItems = 1; // Always show at least one row
                  valueChainData.stages.forEach(stage => {
                    if (stage.categories && stage.categories[category]) {
                      const items = splitContent(stage.categories[category]);
                      maxItems = Math.max(maxItems, items.length);
                    }
                  });
                  
                  // Create rows for each item
                  const rows = [];
                  for (let i = 0; i < maxItems; i++) {
                    rows.push(
                      <Box
                        key={`${category}-row-${i}`}
                        sx={{
                          display: 'flex',
                          borderBottom: i < maxItems - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                          backgroundColor: i % 2 === 0 ? 'background.paper' : alpha(theme.palette.background.default, 0.5),
                        }}
                      >
                        {valueChainData.stages.map((stage, colIndex) => {
                          const items = stage.categories && stage.categories[category] 
                            ? splitContent(stage.categories[category]) 
                            : [];
                          
                          const cellId = `${category}-${i}-${colIndex}`;
                          const isHovered = hoveredCell === cellId;
                          const hasContent = items[i];
                          
                          return (
                            <Box
                              key={colIndex}
                              sx={{
                                width: columnWidth,
                                minWidth: minColumnWidth,
                                p: 2,
                                borderRight: colIndex < valueChainData.stages.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                backgroundColor: isHovered ? getCellHighlightColor(colIndex, valueChainData.stages.length) : 'transparent',
                                '&:after': isHovered ? {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: '10%',
                                  width: '80%',
                                  height: 2,
                                  backgroundColor: getCellHighlightColor(colIndex, valueChainData.stages.length, true)
                                } : {},
                                minHeight: '3rem',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              onMouseEnter={() => setHoveredCell(cellId)}
                              onMouseLeave={() => setHoveredCell(null)}
                            >
                              <EditableText
                                value={items[i] || ''}
                                onSave={(value) => updateCellContent(colIndex, category, i, value)}
                                placeholder="Click to add content..."
                                variant="body2"
                                sx={{ 
                                  width: '100%',
                                  color: hasContent ? 'text.primary' : 'text.disabled'
                                }}
                              />
                              
                              {/* Add item button for empty cells */}
                              {!hasContent && (
                                <IconButton
                                  size="small"
                                  onClick={() => addItemToCategory(colIndex, category)}
                                  sx={{ 
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    opacity: 0.5,
                                    '&:hover': { opacity: 1 }
                                  }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                  return rows;
                })()}
              </React.Fragment>
            ))}
            
            {/* For uncategorized content */}
            {(() => {
              // Check if any stage has uncategorized content
              const hasUncategorizedContent = valueChainData.stages.some(stage => 
                stage.content && splitContent(stage.content).length > 0
              );
              
              const showUncategorized = hasUncategorizedContent || valueChainData.stages.some(stage => !stage.categories || Object.keys(stage.categories).length === 0);
              
              if (!showUncategorized) return null;
              
              return (
                <React.Fragment>
                  {(() => {
                    // Find maximum number of uncategorized items across all stages
                    let maxItems = 1;
                    valueChainData.stages.forEach(stage => {
                      if (stage.content) {
                        const items = splitContent(stage.content);
                        maxItems = Math.max(maxItems, items.length);
                      }
                    });
                    
                    // Create rows for each uncategorized item
                    const rows = [];
                    for (let i = 0; i < maxItems; i++) {
                      rows.push(
                        <Box
                          key={`uncategorized-row-${i}`}
                          sx={{
                            display: 'flex',
                            borderBottom: i < maxItems - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                            backgroundColor: i % 2 === 0 ? 'background.paper' : alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          {valueChainData.stages.map((stage, colIndex) => {
                            const items = stage.content ? splitContent(stage.content) : [];
                            
                            const cellId = `uncategorized-${i}-${colIndex}`;
                            const isHovered = hoveredCell === cellId;
                            const hasContent = items[i];
                            
                            return (
                              <Box
                                key={colIndex}
                                sx={{
                                  width: columnWidth,
                                  minWidth: minColumnWidth,
                                  p: 2,
                                  borderRight: colIndex < valueChainData.stages.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                  transition: 'all 0.2s ease',
                                  position: 'relative',
                                  backgroundColor: isHovered ? getCellHighlightColor(colIndex, valueChainData.stages.length) : 'transparent',
                                  '&:after': isHovered ? {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: '10%',
                                    width: '80%',
                                    height: 2,
                                    backgroundColor: getCellHighlightColor(colIndex, valueChainData.stages.length, true)
                                  } : {},
                                  minHeight: '3rem',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                onMouseEnter={() => setHoveredCell(cellId)}
                                onMouseLeave={() => setHoveredCell(null)}
                              >
                                <EditableText
                                  value={items[i] || ''}
                                  onSave={(value) => {
                                    const newStages = [...valueChainData.stages];
                                    const currentItems = newStages[colIndex].content ? splitContent(newStages[colIndex].content) : [];
                                    currentItems[i] = value;
                                    newStages[colIndex].content = currentItems.filter(item => item.trim() !== '').join(', ');
                                    setValueChainData(prev => ({ ...prev, stages: newStages }));
                                  }}
                                  placeholder="Click to add content..."
                                  variant="body2"
                                  sx={{ 
                                    width: '100%',
                                    color: hasContent ? 'text.primary' : 'text.disabled'
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    }
                    return rows;
                  })()}
                </React.Fragment>
              );
            })()}
          </Paper>
        </Box>
      </Box>
      
      {/* Additional content section */}
      {valueChainData.additionalContent && valueChainData.additionalContent.length > 0 && (
        <Paper
          elevation={2}
          sx={{
            mt: 4,
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: theme.palette.background.paper,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
            }}
          />
          
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 3,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                width: '40%',
                height: 3,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.main,
                bottom: -8,
                left: '30%'
              }
            }}
          >
            Additional Content
          </Typography>
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              gap: 1.5,
              mt: 4
            }}
          >
            {valueChainData.additionalContent.map((content, index) => (
              <Chip
                key={index}
                label={
                  <EditableText
                    value={content}
                    onSave={(value) => {
                      const newContent = [...valueChainData.additionalContent];
                      newContent[index] = value;
                      setValueChainData(prev => ({ ...prev, additionalContent: newContent }));
                    }}
                    placeholder="Edit content..."
                    variant="body2"
                  />
                }
                clickable
                color={index % 2 === 0 ? "primary" : "secondary"}
                variant="outlined"
                onDelete={() => {
                  const newContent = valueChainData.additionalContent.filter((_, i) => i !== index);
                  setValueChainData(prev => ({ ...prev, additionalContent: newContent }));
                }}
                sx={{
                  px: 1,
                  py: 2.5,
                  borderRadius: 6,
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: index % 2 === 0 
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.secondary.main, 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.07)'
                  }
                }}
              />
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                const newContent = [...(valueChainData.additionalContent || []), ''];
                setValueChainData(prev => ({ ...prev, additionalContent: newContent }));
              }}
              sx={{ 
                borderRadius: 6,
                px: 2,
                py: 1
              }}
            >
              Add Content
            </Button>
          </Box>
        </Paper>
      )}

      {/* Render detail modal when a stage is selected */}
      {renderDetailView()}
    </Box>
  );
};

export default EditableValueChainTable;